import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { imagePath } from '../../utils/imagePath';
import ProductRating from './ProductRating';
import ProductActions from './ProductActions';

/**
 * ProductCard - Pure, reusable product card component
 * Follows React Rules: Pure component, idempotent, immutable props
 * 
 * @param {Object} product - Product data object from API
 * @param {Function} onAddToCart - Callback when add to cart is clicked
 * @param {Function} onAddToWishlist - Callback when wishlist is clicked
 * @param {Function} onQuickView - Callback when quick view is clicked
 */
const ProductCard = ({
  product,
  onAddToCart,
  onAddToWishlist,
  onQuickView
}) => {
  const navigate = useNavigate();
  // Handle API response format
  const id = product.id;
  const name = product.name;
  const slug = product.slug;
  const price = parseFloat(product.price || 0);
  const compareAtPrice = product.compare_at_price ? parseFloat(product.compare_at_price) : null;
  const discountPercentage = product.discount_percentage || 0;
  
  // Handle images - API returns primary_image_url and secondary_image_url
  const primaryImage = product.primary_image_url || product.primary_image || product.image || imagePath('product/large-size/1.jpg');
  const secondaryImage = product.secondary_image_url || product.secondary_image || null;
  
  // Handle manufacturer - API returns object with name
  const manufacturer = product.manufacturer?.name || product.manufacturer || null;
  
  // Handle rating - API returns average_rating
  const rating = product.average_rating || product.rating || 0;
  
  // Handle isNew - API returns is_new
  const isNew = product.is_new || product.isNew || false;

  const productUrl = `/store/product/${slug || id}`;
  const manufacturerUrl = manufacturer ? `/store/shop?manufacturer=${encodeURIComponent(manufacturer)}` : `/store/shop`;

  // Debug - log the actual URLs being generated
  if (!slug && !id) {
    console.error('ProductCard: Product has no slug or id!', product);
  } else {
    console.log('ProductCard URL:', productUrl, 'for product:', name);
  }

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart(id);
    }
  };

  const handleAddToWishlist = () => {
    if (onAddToWishlist) {
      onAddToWishlist(id);
    }
  };

  const handleQuickView = () => {
    if (onQuickView) {
      onQuickView(id);
    }
  };

  const handleCardClick = (e) => {
    // Ignore clicks on action buttons/links inside the card
    if (e.target.closest('.product-action') || e.target.closest('.add-to-cart') || e.target.closest('.add-to-wishlist')) {
      return;
    }
    e.preventDefault();
    navigate(productUrl);
  };

  return (
    <div className="single-product-wrap" role="button" onClick={handleCardClick} tabIndex={0}>
      <div className="product-image">
        <Link to={productUrl}>
          <img 
            className="primary-image" 
            src={primaryImage} 
            alt={name}
            onError={(e) => {
              e.target.src = imagePath('product/large-size/1.jpg');
            }}
          />
          {secondaryImage && (
            <img 
              className="secondary-image" 
              src={secondaryImage} 
              alt={name}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
        </Link>
        {isNew && <span className="sticker">New</span>}
        {discountPercentage > 0 && (
          <span className="sticker discount-sticker">-{discountPercentage}%</span>
        )}
      </div>
      <div className="product_desc">
        <div className="product_desc_info">
          <div className="product-review">
            {manufacturer && (
              <h5 className="manufacturer">
                <Link to={manufacturerUrl} title={`View all ${manufacturer} products`}>
                  {manufacturer}
                </Link>
              </h5>
            )}
            <ProductRating rating={rating} />
          </div>
          <h4>
            <Link className="product_name" to={productUrl}>
              {name}
            </Link>
          </h4>
          <div className="price-box">
            <span className="new-price">
              PKR {price.toFixed(2)}
            </span>
            {compareAtPrice && compareAtPrice > price && (
              <>
                <span className="old-price">PKR {compareAtPrice.toFixed(2)}</span>
                {discountPercentage > 0 && (
                  <span className="discount-percentage">-{discountPercentage}%</span>
                )}
              </>
            )}
          </div>
        </div>
        <ProductActions
          productId={id}
          onAddToCart={handleAddToCart}
          onAddToWishlist={handleAddToWishlist}
          onQuickView={handleQuickView}
        />
      </div>
    </div>
  );
};

export default ProductCard;

